// @ts-nocheck
/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { Link, useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import { showSuiErrorSnackbar } from "components/SuiSnackbar";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import Separator from "layouts/authentication/components/Separator";

// Images
import curved9 from "assets/images/curved-images/curved9.jpg";

import { useAuth } from "auth";
import { AppleSignInButton, isAppleSignInConfigured } from "auth/apple-sign-in-button";
import { GoogleSignInButton, isGoogleSignInConfigured } from "auth/google-sign-in-button";
import { isValidPassword, normalizeAuthIdentifier } from "auth/auth-credentials";
import { useLocale } from "lib/i18n/client";
import { resolveTooManyAttemptsMessage } from "auth/too-many-attempts";
import { getTranslations } from "lib/i18n/dictionary";
import { withLocale } from "lib/i18n/path";
import { useLocalizedNavigate } from "lib/i18n/navigation";

function Basic() {
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { signIn, signInWithApple, signInWithGoogle, isAuthenticated, isInitializing } = useAuth();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const t = translations.auth;
  const authPage = translations.authPage;

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [isIdentifierTouched, setIsIdentifierTouched] = useState(false);
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAppleSubmitting, setIsAppleSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const normalizedIdentifier = normalizeAuthIdentifier(identifier);
  const normalizedPassword = password.trim();
  const identifierHasValue = identifier.trim().length > 0;
  const passwordHasValue = normalizedPassword.length > 0;
  const identifierError = isIdentifierTouched && identifierHasValue && !normalizedIdentifier;
  const passwordTooShort =
    isPasswordTouched && passwordHasValue && !isValidPassword(normalizedPassword);

  const redirectPath =
    typeof location.state?.from === "string" ? location.state.from : "/dashboard";
  const isAuthMutationPending = isSubmitting || isAppleSubmitting || isGoogleSubmitting;

  useEffect(() => {
    if (!isInitializing && isAuthenticated && !isAuthMutationPending) {
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthMutationPending, isAuthenticated, isInitializing, navigate, redirectPath]);

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const handleIdentifierBlur = () => {
    setIsIdentifierTouched(true);
    if (normalizedIdentifier && normalizedIdentifier !== identifier) {
      setIdentifier(normalizedIdentifier);
    }
  };
  const handlePasswordBlur = () => setIsPasswordTouched(true);

  const handleAppleCredential = async ({ idToken, firstName, lastName }) => {
    setErrorMessage("");
    setIsAppleSubmitting(true);

    try {
      const authResult = await signInWithApple(
        {
          idToken,
          firstName,
          lastName,
        },
        {
          remember: rememberMe,
        }
      );

      navigate(
        authResult.isNewAccount && authResult.activeProfileUuid
          ? `/profile/${authResult.activeProfileUuid}`
          : redirectPath,
        { replace: true }
      );
    } finally {
      setIsAppleSubmitting(false);
    }
  };

  const handleGoogleCredential = async (idToken) => {
    setErrorMessage("");
    setIsGoogleSubmitting(true);

    try {
      const authResult = await signInWithGoogle(
        {
          idToken,
        },
        {
          remember: rememberMe,
        }
      );

      navigate(
        authResult.isNewAccount && authResult.activeProfileUuid
          ? `/profile/${authResult.activeProfileUuid}`
          : redirectPath,
        { replace: true }
      );
    } finally {
      setIsGoogleSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!normalizedIdentifier || !normalizedPassword) {
      setErrorMessage(authPage.signInMissingFields);
      return;
    }

    if (!isValidPassword(normalizedPassword)) {
      setErrorMessage(t.passwordTooShort);
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);
    if (normalizedIdentifier !== identifier) {
      setIdentifier(normalizedIdentifier);
    }

    try {
      await signIn(
        {
          username: normalizedIdentifier,
          password: normalizedPassword,
        },
        {
          remember: rememberMe,
        }
      );

      navigate(redirectPath, { replace: true });
    } catch (error) {
      const tooManyAttemptsMessage = resolveTooManyAttemptsMessage(
        error,
        authPage.tooManyAttempts
      );
      if (tooManyAttemptsMessage) {
        setErrorMessage(tooManyAttemptsMessage);
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(authPage.signInFailed);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BasicLayout title={authPage.heroTitle} description={authPage.heroDescription} image={curved9}>
      <Card>
        <SoftBox p={3} mb={1} textAlign="center">
          <SoftTypography variant="h5" fontWeight="medium">
            {authPage.signInHeading}
          </SoftTypography>
        </SoftBox>
        <SoftBox p={3}>
          {(isAppleSignInConfigured || isGoogleSignInConfigured) && (
            <>
              <SoftBox mb={3} display="flex" flexDirection="column" gap={1.5}>
                {isAppleSignInConfigured && (
                  <AppleSignInButton
                    isSubmitting={isAppleSubmitting}
                    onCredential={handleAppleCredential}
                    onError={setErrorMessage}
                    authFailedMessage={t.authFailed}
                    credentialMissingMessage={t.appleCredentialMissing}
                    label={t.continueWithApple}
                    loadFailedMessage={t.appleLoadFailed}
                  />
                )}
                {isGoogleSignInConfigured && (
                  <GoogleSignInButton
                    isSubmitting={isGoogleSubmitting}
                    onCredential={handleGoogleCredential}
                    onError={setErrorMessage}
                    authFailedMessage={t.authFailed}
                    credentialMissingMessage={t.googleCredentialMissing}
                    label={t.continueWithGoogle}
                    loadFailedMessage={t.googleLoadFailed}
                  />
                )}
              </SoftBox>
              <Separator label={authPage.orDivider} />
            </>
          )}
          <SoftBox component="form" role="form" onSubmit={handleSubmit}>
            <SoftBox mb={2}>
              <SoftInput
                type="text"
                placeholder={t.identifier}
                autoComplete="username"
                inputProps={{
                  inputMode: identifier.includes("@") ? "email" : "tel",
                }}
                error={identifierError}
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                onBlur={handleIdentifierBlur}
              />
              {identifierError && (
                <SoftTypography variant="caption" color="error" fontWeight="medium">
                  {t.identifierInvalid}
                </SoftTypography>
              )}
            </SoftBox>
            <SoftBox mb={2}>
              <SoftInput
                type="password"
                placeholder={t.password}
                autoComplete="current-password"
                error={passwordTooShort}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={handlePasswordBlur}
              />
              <SoftTypography
                variant="caption"
                color={passwordTooShort ? "error" : "text"}
                fontWeight={passwordTooShort ? "medium" : "regular"}
              >
                {passwordTooShort ? t.passwordTooShort : t.passwordHintSignIn}
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" alignItems="center">
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <SoftTypography
                variant="button"
                fontWeight="regular"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none" }}
              >
                &nbsp;&nbsp;{authPage.rememberMe}
              </SoftTypography>
            </SoftBox>
            <SoftBox mt={4} mb={1}>
              <SoftButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={
                  isSubmitting ||
                  isAppleSubmitting ||
                  isGoogleSubmitting ||
                  !identifier.trim() ||
                  !normalizedPassword
                }
              >
                {isSubmitting ? authPage.signInSubmitting : authPage.signInSubmit}
              </SoftButton>
            </SoftBox>
            <SoftBox mt={3} textAlign="center">
              <SoftTypography variant="button" color="text" fontWeight="regular">
                {authPage.noAccountYet}&nbsp;
                <SoftTypography
                  component={Link}
                  to={withLocale("/sign-up", locale)}
                  variant="button"
                  color="dark"
                  fontWeight="bold"
                  textGradient
                >
                  {t.signUp}
                </SoftTypography>
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
